import React from 'react';
import { useTranslation } from 'react-i18next';

import { ActionButtonModal } from 'src/legacy/components/Modals';
import { useManageCompanyClientsMutation } from 'src/services/api/companiesApi';

type PendingRemoval = {
  companyId: string;
  clientId: string;
  companyName: string;
  isLastCompany: boolean;
} | null;

export type RemoveClientWithWarningParams = {
  companyId: string;
  clientId: string;
  companyName: string;
  isLastCompany: boolean;
};

export type UseDataLossWarningReturn = {
  removeClientWithWarning: (
    params: RemoveClientWithWarningParams,
  ) => Promise<boolean>;
  cancelRemoval: () => void;
  pendingRemoval: PendingRemoval;
  DataLossWarningModal: React.FC;
};

function DataLossWarningModalComponent({
  pendingRemoval,
  onCancel,
  onConfirm,
}: {
  pendingRemoval: PendingRemoval;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();

  if (!pendingRemoval) return null;

  return (
    <ActionButtonModal
      open
      onClose={onCancel}
      positiveAction
      onPositiveActionPerformed={onConfirm}
      title={t(($) => $.messages.remove_company_warning.title)}
      positiveActionText={t(($) => $.common.confirm)}
      mainComponent={
        pendingRemoval.isLastCompany
          ? t(($) => $.messages.remove_company_warning.description_last, {
              companyName: pendingRemoval.companyName,
            })
          : t(($) => $.messages.remove_company_warning.description_single, {
              companyName: pendingRemoval.companyName,
            })
      }
    />
  );
}

export const useDataLossWarning = (): UseDataLossWarningReturn => {
  const [manageCompanyClients] = useManageCompanyClientsMutation();
  const [pendingRemoval, setPendingRemoval] =
    React.useState<PendingRemoval>(null);

  const removeClientWithWarning = React.useCallback(
    async (params: RemoveClientWithWarningParams): Promise<boolean> => {
      try {
        const result = await manageCompanyClients({
          companyId: params.companyId,
          addClientIds: [],
          removeClientIds: [params.clientId],
          confirmDataLoss: false,
        }).unwrap();

        const hasWarning = result.removeClientErrors?.some(
          (e) =>
            e.error.type === 'record_conflict' &&
            e.error.message === 'data_loss_warning',
        );

        if (hasWarning) {
          setPendingRemoval(params);
          return false;
        }
        return true;
      } catch {
        // Network/server errors are handled by companiesApi's onQueryStarted
        return false;
      }
    },
    [manageCompanyClients],
  );

  const confirmRemoval = React.useCallback(async () => {
    if (!pendingRemoval) return;
    setPendingRemoval(null);
    try {
      await manageCompanyClients({
        companyId: pendingRemoval.companyId,
        addClientIds: [],
        removeClientIds: [pendingRemoval.clientId],
        confirmDataLoss: true,
      });
    } catch {
      // Network/server errors are handled by companiesApi's onQueryStarted
    }
  }, [manageCompanyClients, pendingRemoval]);

  const cancelRemoval = React.useCallback(() => {
    setPendingRemoval(null);
  }, []);

  const DataLossWarningModal: React.FC = React.useCallback(
    () => (
      <DataLossWarningModalComponent
        pendingRemoval={pendingRemoval}
        onCancel={cancelRemoval}
        onConfirm={confirmRemoval}
      />
    ),
    [pendingRemoval, cancelRemoval, confirmRemoval],
  );

  return {
    removeClientWithWarning,
    cancelRemoval,
    pendingRemoval,
    DataLossWarningModal,
  };
};
